import { store } from '../store'
import Vue from 'vue'
import Router from 'vue-router'
import { parseQuery, stringifyQuery } from './override'
import { scrollBehavior } from './scrollBehavior'
import { Breadcrumbs } from '../plugins/breadcrumbs-plugin'
import { ThirdPartyToolsManager } from '@/utils/thirdPartyToolsManager' // Is going to be deprecated
import { determineSideNavVisibility } from '@/utils/determineSideNavVisibility'
import { generateAndStoreSideNavSettings } from '@/sideNav'

import {
  SetupGuideEnum,
  BaseRouteEnum,
  OrdersEnum,
  CustomersEnum
} from '../enums/modules/routes'
import OnboardingRoutesEnum from '../enums/modules/routes/onboarding'
import WavePickingAppRoutes from './wavepicking-app'
import SetupGuideRoutes from './setupGuide'
import RulesRoutes from './rules'
import OrdersRoutes from './orders'
import WarehouseReceivingOrdersRoutes from './warehouseReceivingOrders'
import ProductsRoutes from './products'
import PricingRoutes from './pricing'
import SetupDashboardRoutes, { shouldUserOnboard } from './dashboard'
import CreateOrderRoutes from './createOrder'
import AdminRoutes from './admin'
import FourOhFourWrapper from '../components/general/FourOhFourWrapper'
import FiveHundredWrapper from '../components/general/FiveHundredWrapper'
import ForbiddenWrapper from '../components/general/ForbiddenWrapper'
import SplashScreen from '@models/splashScreen'
import ShipbobAppSettings from '@/appSettings/appSetting'
import { encodeAndStoreReturnUrl, handleRedirect } from '@/utils/redirect'
import WorkOrderRoutes from './workOrders'
import { ReturnMFERoutes } from './returns'
import IntegrationsRoutes from './integrations'
import InventoryRoutes from './inventory'
import LocationsRoute from './locations'
import ShopifyInstallRoute from './shopifyInstall'
import PowerBiRoute from './powerBi'
import GiftNotesSignup from './giftNotesSignup'
import Onboarding from './onboarding'
import ProductBadgingRoutes from './productBadging'
import ProductCatalogRoutes from './productCatalog'
import InventoryUIRoutes from './inventoryUI'
import OrderShipmentManagementRoutes from './orderShipmentManagement'
import BillingRoutes from './billing'
import UserManagementRoutes from './userManagement'
import B2BOrdersRoutes from './b2bOrders'
import { ChatbotWrapper } from '@/utils/ChatbotWrapper'
import { callback } from '@/auth/authHandler'
import FulfillmentSettingsRoutes from './fulfillmentSettings'
import PaymentRoutes from './payment'
import LabelingRoutes from './labeling'
import CustomersRoutes from './customers'
import IntegrationsUiRoutes from './integrationsUI'
import AnalyticsUiRoutes from './analyticsUI'
import ParagonRoutes from './paragonUI'
import CartbobRoutes from './cartbob'
import ReceivingRoutes from './receivingUI'



import healthCheckRoute from './healthCheckRoute'

import { recursivelyAddTelemetry } from './telemetry'

const DASHBOARD_ROUTE = process.env.USE_PHOTOPRINT === '1' ? ShipbobAppSettings.dashboardPath : `/app/merchant/${ShipbobAppSettings.dashboardPath}`

Vue.use(Router)
Vue.use(Breadcrumbs)

// Here we can import route definitions from separate modules - This can be helpful if a route has many child routes
// To do so just export an array of route definitions from a separate module, import that list here, and add it
//  to the moduleRoutes list below

let moduleRoutes = [
  RulesRoutes,
  WavePickingAppRoutes,
  SetupGuideRoutes,
  OrdersRoutes,
  WarehouseReceivingOrdersRoutes,
  ProductsRoutes,
  PricingRoutes,
  SetupDashboardRoutes,
  CreateOrderRoutes,
  AdminRoutes,
  WorkOrderRoutes,
  ReturnMFERoutes,
  IntegrationsRoutes,
  InventoryRoutes,
  LocationsRoute,
  ShopifyInstallRoute,
  GiftNotesSignup,
  PowerBiRoute,
  ProductBadgingRoutes,
  Onboarding,
  ProductCatalogRoutes,
  InventoryUIRoutes,
  OrderShipmentManagementRoutes,
  BillingRoutes,
  UserManagementRoutes,
  B2BOrdersRoutes,
  FulfillmentSettingsRoutes,
  PaymentRoutes,
  LabelingRoutes,
  CustomersRoutes,
  IntegrationsUiRoutes,
  AnalyticsUiRoutes,
  ParagonRoutes,
  CartbobRoutes,
  ReceivingRoutes
]
moduleRoutes = [].concat.apply([], moduleRoutes)

const baseRoutes = [
  {
    path: '/',
    name: BaseRouteEnum.Dashboard,
    meta: { showSideNav: false },
    beforeEnter (to, from, next) {
      const integrationType = from.query.type
      let qs = ''
      if (integrationType) {
        qs = '?type=integrationType'
      }
      // redirecting with a full page reload because after register we do not have the full user object from fetchLoggedInAndImpersonatedBasicUserData
      // reloading the window causes that fetch to happen properly
      window.location.replace(DASHBOARD_ROUTE + qs)
    }
  },
  {
    path: '/Error',
    name: BaseRouteEnum.FiveHundred,
    component: FiveHundredWrapper
  },
  {
    path: '*',
    name: BaseRouteEnum.FourOhFour,
    component: FourOhFourWrapper,
    meta: { showSideNav: false }
  },
  {
    path: '/Forbidden',
    name: BaseRouteEnum.Forbidden,
    component: ForbiddenWrapper
  },
  healthCheckRoute
]
const routes = baseRoutes.concat(moduleRoutes).map(recursivelyAddTelemetry)

const router = new Router({
  base: ShipbobAppSettings.vueRouterBase,
  routes,
  parseQuery,
  stringifyQuery,
  scrollBehavior
})

// ******************************************************
// *************** Route Guard Functions ****************
// ******************************************************
const setupUser = async () => {
  if (store.getters['user/getUser'].isDefault()) {
    await store.dispatch('user/getUser')
  }
}

const setupThirdPartyTools = async to => {
  if (!ThirdPartyToolsManager.initialized) {
    await store.dispatch('onboarding/loadOnboardingState', to.query)

    ThirdPartyToolsManager.init({
      appSettings: store.state.settings,
      userData: store.state.user.user, // the call above to getUser sets this data up
      impersonatedUser: store.state.user.impersonatedUser,
      loggedInUser: store.state.user.loggedInUser,
      accessToken: store.state.user.accessToken
    })
  }
  ThirdPartyToolsManager.routeTo(to.name, routeRequiresAuth(to.name))
}

const routeRequiresAuth = route => {
  const routesThatDontRequireAuth = [
    SetupGuideEnum.SignUp,
    SetupGuideEnum.Login,
    SetupGuideEnum.ResetYourPassword,
    SetupGuideEnum.ForgotPassword,
    SetupGuideEnum.CheckYourEmail,
    SetupGuideEnum.SignUpWithToken,
    SetupGuideEnum.SignUpPreLaunch,
    SetupGuideEnum.FreeAccount,
    BaseRouteEnum.FourOhFour,
    BaseRouteEnum.Forbidden,
    SetupGuideEnum.Initialize,
    SetupGuideEnum.Logout,
    SetupGuideEnum.Refresh,
    'HealthCheck'
  ]
  return !routesThatDontRequireAuth.includes(route)
}

const authenticated = async routeTo => {
  if (!routeRequiresAuth(routeTo)) {
    return true
  }
  return await store.dispatch('authenticate')
}

const noAuthHides = () => {
  store.dispatch('toggleSideNav', false)
  ThirdPartyToolsManager.ZendeskWrapper.hideZendesk()
  ThirdPartyToolsManager.disablePendoGuides()
  ChatbotWrapper.hide()
}

const isOnboardingComplete = () => {
  return store.getters['onboarding/completed']
}

const isOnboardingShown = () => {
  return store.getters['onboarding/onboardingShown']
}

const handleNextOnboarding = async (to, next, shouldPreFetchToRoute) => {
  const shouldOnboard = await shouldUserOnboard(to.query)
  if (shouldOnboard) {
    if (to.name !== OnboardingRoutesEnum.Onboarding) {
      next({ name: OnboardingRoutesEnum.Onboarding })
    } else {
      next()
    }
  } else {
    if (routeRequiresAuth(to.name)) {
      ThirdPartyToolsManager.enablePendoGuides()
      ThirdPartyToolsManager.enableSfChat()
    }
    next()
  }
}

Breadcrumbs.setup({
  router,
  root: BaseRouteEnum.Dashboard,
  exclude: [BaseRouteEnum.FourOhFour],
  anchors: [OrdersEnum.OrdersWithView, CustomersEnum.Customers]
})
router.beforeEach(async (to, from, next) => {
  if (from.name === OnboardingRoutesEnum.Onboarding && isOnboardingShown() && !isOnboardingComplete()) {
    next(false)
  } else {
    Breadcrumbs.push(to)
    const routeFrom = from.name
    const routeTo = to.name
    if (window.location.href.includes('signin-callback?code=') && !store.state.authenticated) {
      SplashScreen.add('users')
      const loginHandleRedirect = (route) => {
        if (isOnboardingComplete()) {
          handleRedirect(route)
        } else {
          handleNextOnboarding(to, next, false)
        }
      }
      await callback(loginHandleRedirect)
    } else if (!(await authenticated(routeTo))) {
      store.dispatch('user/resetUser')
      store.dispatch('resetAuthentication')
      noAuthHides()
      next(encodeAndStoreReturnUrl(to))
    } else if (routeRequiresAuth(routeTo)) {
      // this is our authenticated entry point
      await store.dispatch('authenticate')
      const shouldLoadSplashScreen = await SplashScreen.preFetchComponents.users(to.name)
      if (shouldLoadSplashScreen) {
        SplashScreen.add('users')
      }
      await setupUser() // now does the getUser call that used to come after determineSideNavVisibility
      await generateAndStoreSideNavSettings(store)
      await setupThirdPartyTools(to)
      determineSideNavVisibility(to)
      const shouldPreFetchToRoute = SplashScreen.preFetchComponents[to.name]
      if (shouldPreFetchToRoute && shouldPreFetchToRoute()) {
        SplashScreen.add(to.name)
      }
      if (shouldLoadSplashScreen) {
        SplashScreen.remove()
      }

      handleNextOnboarding(to, next, shouldPreFetchToRoute)
    } else if (!routeRequiresAuth(routeTo) && !routeRequiresAuth(routeFrom)) {
      noAuthHides()

      next()
    } else {
      store.dispatch('user/resetUser')
      store.dispatch('resetAuthentication')
      noAuthHides()
      next()
    }
  }
})

export default router
